import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Bot, Pause, Play, Power, Send } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth, hasRole } from '../lib/auth';
import { useI18n } from '../lib/i18n';

/**
 * LA TARJETA DEL AUTO FOLLOW-UP EN UN CLAIM — vive en la tarjeta de Supplementing del board y en
 * la pestaña Supplements del drawer. Dice en qué va la máquina para ESTE claim (activa, pausada,
 * frenada y por qué), el historial de lo que salió, y los botones: apagar, pausar/reanudar,
 * arrancar a mano y "mandar el siguiente ya" (manager+).
 */
export type AutoFollowUpState = {
  config: { enabled: boolean; dry_run: boolean; every_days: number; max_sends: number; env_enabled: boolean };
  sequence: {
    id: string; to_address: string; subject: string; sent_by: string | null; estimate_sent_at: string;
    status: 'active' | 'paused' | 'stopped'; stop_reason: string | null; stopped_at: string | null; stopped_by: string | null;
    reply_email_id: string | null; reply_from: string | null; reply_subject: string | null;
    paused_at: string | null; paused_by: string | null;
    sends_count: number; last_sent_at: string | null; next_due_at: string | null; auto_reply_at: string | null; started_by: string | null;
    days_since_estimate: number;
  } | null;
  sends: Array<{ id: string; seq: number | null; template_index: number; days_since_estimate: number; email_id: string | null; status: string; error: string | null; subject: string | null; body: string | null; manual: boolean; sent_at: string; email_status: string | null; delivered_at: string | null; opened_at: string | null }>;
  previous: number;
  can_start: boolean;
  statuses: string[];
};

const fecha = (d: string | null | undefined) => (d ? new Date(d).toLocaleDateString(localeUI()) : '—');
const fechaHora = (d: string | null | undefined) => (d ? new Date(d).toLocaleString(localeUI(), { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

export function AutoFollowUpCard({ claimId, compact, onChanged }: { claimId: string; compact?: boolean; onChanged?: () => void }) {
  const { t } = useI18n();
  const { me } = useAuth();
  const [data, setData] = useState<AutoFollowUpState | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [verTodo, setVerTodo] = useState(false);
  const [abierto, setAbierto] = useState<string | null>(null);

  const load = () => api.get<AutoFollowUpState>(`/api/claims/${claimId}/supplements/auto-followup`).then((d) => { setData(d); setErr(null); }).catch((e) => setErr(e instanceof ApiError ? e.message : 'error'));
  useEffect(() => { let alive = true; api.get<AutoFollowUpState>(`/api/claims/${claimId}/supplements/auto-followup`).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setErr(e instanceof ApiError ? e.message : 'error'); }); return () => { alive = false; }; }, [claimId]);

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 2500); };
  async function accion(path: string, ok: string) {
    setBusy(true);
    try { await api.post(`/api/claims/${claimId}/supplements/auto-followup/${path}`, {}); flash(ok); await load(); onChanged?.(); }
    catch (e) { flash(e instanceof ApiError ? `${t('afu.err')}: ${e.message}` : t('afu.err')); }
    finally { setBusy(false); }
  }

  if (err) return <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('afu.loadErr')}</div>;
  if (!data) return <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('common.loading')}</div>;
  const s = data.sequence;
  const vivo = s && s.status !== 'stopped';
  const maquinaApagada = !data.config.enabled || !data.config.env_enabled;
  const razon = (r: string | null) => (r ? t(`afu.reason.${r}`) : '');

  const pill = !s ? <span className="badge gray">{t('afu.none')}</span>
    : s.status === 'active' ? <span className="badge green">{t('afu.active')}</span>
      : s.status === 'paused' ? <span className="badge orange">{t('afu.paused')}</span>
        : <span className="badge gray">{t('afu.stopped')} · {razon(s.stop_reason)}</span>;

  return (
    <div style={{ display: 'grid', gap: 8, fontSize: 'var(--fs-xs)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Bot size={15} strokeWidth={1.75} />
        <strong>{t('afu.title')}</strong>
        {pill}
        {s && vivo && data.config.dry_run && <span className="badge orange" title={t('afu.dryRunHint')}>{t('afu.dryRun')}</span>}
        {s && vivo && maquinaApagada && <span className="badge gray" title={t('afu.offHint')}>{t('afu.systemOff')}</span>}
        <span style={{ flex: 1 }} />
        {msg && <span className="muted">{msg}</span>}
      </div>

      {s && (
        <div className="muted" style={{ display: 'grid', gap: 2, fontSize: 'var(--fs-2xs)' }}>
          <div>{t('afu.to')}: <span style={{ fontFamily: 'var(--font-mono)' }}>{s.to_address}</span> · {t('afu.subject')}: “{s.subject}”</div>
          <div>
            {t('afu.estimateSent')}: {fecha(s.estimate_sent_at)} ({t('afu.daysAgo', { n: String(s.days_since_estimate) })}){s.sent_by ? ` · ${t('afu.by')} ${s.sent_by}` : ''}
            {' · '}{t('afu.sent')}: <strong>{s.sends_count}/{data.config.max_sends}</strong>
            {s.status === 'active' && <> · {t('afu.next')}: {s.next_due_at && new Date(s.next_due_at).getTime() > Date.now() ? fechaHora(s.next_due_at) : t('afu.nextSlot')}</>}
            {s.last_sent_at && <> · {t('afu.last')}: {fechaHora(s.last_sent_at)}</>}
          </div>
          {s.auto_reply_at && <div>⏸ {t('afu.autoReplySeen', { d: fecha(s.auto_reply_at) })}</div>}
          {s.status === 'paused' && <div>{t('afu.pausedBy', { who: s.paused_by ?? '—', d: fecha(s.paused_at) })}</div>}
          {s.status === 'stopped' && (
            <div>
              {t('afu.stoppedOn', { d: fecha(s.stopped_at) })}{s.stopped_by ? ` · ${s.stopped_by}` : ''}
              {s.reply_email_id && (
                <> · <a href={`/claims/${claimId}?tab=email&thread=insurance&email=${s.reply_email_id}`} onClick={(e) => e.stopPropagation()}>{t('afu.seeReply')}</a>
                  {s.reply_from ? ` (${s.reply_from})` : ''}</>
              )}
            </div>
          )}
        </div>
      )}
      {!s && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{data.can_start ? t('afu.noneHintStart') : t('afu.noneHint')}</div>}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {vivo && s?.status === 'active' && <button className="btn ghost sm" disabled={busy} onClick={() => accion('pause', t('afu.pausedOk'))}><Pause size={13} strokeWidth={1.75} /> {t('afu.pause')}</button>}
        {vivo && s?.status === 'paused' && <button className="btn ghost sm" disabled={busy} onClick={() => accion('resume', t('afu.resumedOk'))}><Play size={13} strokeWidth={1.75} /> {t('afu.resume')}</button>}
        {vivo && <button className="btn ghost sm" disabled={busy} style={{ color: 'var(--red)' }} onClick={() => { if (window.confirm(t('afu.stopConfirm'))) void accion('stop', t('afu.stoppedOk')); }}><Power size={13} strokeWidth={1.75} /> {t('afu.stop')}</button>}
        {!vivo && data.can_start && <button className="btn ghost sm" disabled={busy} onClick={() => accion('start', t('afu.startedOk'))}><Play size={13} strokeWidth={1.75} /> {t('afu.start')}</button>}
        {vivo && s?.status === 'active' && hasRole(me, 'manager') && (
          <button className="btn ghost sm" disabled={busy} title={t('afu.sendNowHint')} onClick={() => { if (window.confirm(data.config.dry_run ? t('afu.sendNowDryConfirm') : t('afu.sendNowConfirm'))) void accion('send-now', t('afu.sentOk')); }}>
            <Send size={13} strokeWidth={1.75} /> {t('afu.sendNow')}
          </button>
        )}
      </div>

      {data.sends.length > 0 && (
        <div style={{ display: 'grid', gap: 3, fontSize: 'var(--fs-2xs)' }}>
          {(verTodo || compact ? data.sends : data.sends.slice(0, 5)).map((e) => (
            <div key={e.id} style={{ display: 'grid', gap: 2 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <span className={`badge ${e.status === 'sent' ? 'green' : e.status === 'dry_run' ? 'orange' : 'gray'}`} style={{ fontSize: 'var(--fs-2xs)' }}>
                  {e.status === 'dry_run' ? t('afu.wouldSend') : e.status === 'sent' ? `#${e.seq}` : t(`afu.sendStatus.${e.status}`)}
                </span>
                <span>{fechaHora(e.sent_at)}</span>
                <span className="muted">· {t('afu.template')} {e.template_index + 1} · {t('afu.dayN', { n: String(e.days_since_estimate) })}{e.manual ? ` · ${t('afu.manual')}` : ''}</span>
                {e.email_status && e.email_status !== 'sent' && <span className="muted">· {e.email_status}</span>}
                {e.opened_at && <span className="muted" title={fechaHora(e.opened_at)}>· {t('afu.opened')}</span>}
                <span style={{ flex: 1 }} />
                {e.email_id && <a href={`/claims/${claimId}?tab=email&thread=insurance&email=${e.email_id}`} onClick={(ev) => ev.stopPropagation()}>{t('afu.open')}</a>}
                {e.body && <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)', padding: '0 6px' }} onClick={() => setAbierto(abierto === e.id ? null : e.id)}>{abierto === e.id ? t('afu.hide') : t('afu.text')}</button>}
              </div>
              {abierto === e.id && e.body && <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0, padding: 8, background: 'var(--surface-2)', borderRadius: 6 }}>{e.body.split('\n\nOn ')[0]}</pre>}
              {e.error && <div style={{ color: 'var(--red)' }}>{e.error}</div>}
            </div>
          ))}
          {!compact && data.sends.length > 5 && <button className="btn ghost sm" style={{ justifySelf: 'start', fontSize: 'var(--fs-2xs)' }} onClick={() => setVerTodo((v) => !v)}>{verTodo ? t('afu.showLess') : t('afu.showAll', { n: String(data.sends.length) })}</button>}
        </div>
      )}
      {data.previous > 0 && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('afu.previousRounds', { n: String(data.previous) })}</div>}
    </div>
  );
}
